import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal } from '@folio/stripes-components';
import { AppIcon, useOkapiKy, withStripes } from '@folio/stripes-core';
import EditTranslationsForm from './EditTranslationsForm';

function EditTranslationsManager({
  open,
  onClose,
  item,
  currentLocale,
  category,
  stripes,
  onSaved,
}) {
  const intl = useIntl();
  const okapiKy = useOkapiKy();

  const appName = item.translationKey
    ? item.translationKey.substr(0, item.translationKey.indexOf('.'))
    : '';
  const tableName = item.translationKey
    ? item.translationKey.split('.', 2)[1]
    : '';

  const onFormSubmit = async (values) => {
    const newValue = values.localeTranslation || '';

    try {
      if (item.id) {
        // update existing record
        await okapiKy.put(`translation-keys/${item.id}`, {
          json: {
            ...item,
            value: newValue,
          },
        });
      } else {
        // create new record
        await okapiKy.post('translation-keys', {
          json: {
            translationKey: item.translationKey,
            categoryName: category,
            localeValue: currentLocale,
            value: newValue,
          },
        });
      }

      // update react-intl on the fly if current UI locale matches
      if (intl.locale === currentLocale) {
        stripes.setTranslations({ [item.translationKey]: newValue });
      }

      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to save translation key', e);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      label={
        <AppIcon app="translations" size="small">
          {category === 'libTranslations' ? (
            <FormattedMessage
              id="ui-translations.translationsModal.label"
              defaultMessage="{appName} App - Translation of {tableName}"
              values={{
                tableName: (
                  <FormattedMessage
                    id={`ui-translations.filters.TableFilter.${tableName}`}
                    defaultMessage={tableName}
                  />
                ),
                appName: (
                  <FormattedMessage
                    id={`${appName}.meta.title`}
                    defaultMessage={appName.replace('ui-', '')}
                  />
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id="ui-translations.translationsModal.otherCategories.label"
              defaultMessage="{appName} App"
              values={{
                appName: (
                  <FormattedMessage
                    id={`${appName}.meta.title`}
                    defaultMessage={appName.replace('ui-', '')}
                  />
                ),
              }}
            />
          )}
        </AppIcon>
      }
    >
      <EditTranslationsForm
        onSubmit={onFormSubmit}
        initialValues={{ localeTranslation: item.localeTranslation }}
        onClose={onClose}
        item={item}
        currentLocale={currentLocale}
      />
    </Modal>
  );
}

EditTranslationsManager.propTypes = {
  stripes: PropTypes.object,
  item: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  currentLocale: PropTypes.string,
  category: PropTypes.string.isRequired,
  onSaved: PropTypes.func,
};

export default withStripes(EditTranslationsManager);
